import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {RESET} from 'messaging/message-types.js';
import {findParameterWithContext} from './findParameter.js';
import {shuffle} from 'utils/ShuffleArray.js';
import {PARAMS_TO_IGNORE, getTrackParameters} from './getTrackParameters.js';
import {setEffectParameterValues} from 'actions/index.js';

export default superclass => class extends superclass  {
    _parameters = [];
    initialParameterValues = [];
    initialParameterDepths = [];
    depthParameters = [];

    setupNodeMessaging() {
        this.newInlet(RESET, () => {
            this.stop();
            this.clear();
        });
    }

    newDepthParameter(parameter) {
        let depthParameter = new AbstractParameter(
            "depth", this, (o,x) => x,
            {
                units: "%",
                min: 0,
                max: 100,
                defaultValue: 100
            });

        if (this.context) {
            depthParameter.setContext(this.context);
        }
        this.registerParameter(depthParameter);
        return depthParameter;
    }

    getDepth(i) {
        return this.depthParameters[i] ?
            this.depthParameters[i].get() : 0;
    }

    setupRampParameters() {
        this.rampTimeParameter = new AbstractParameter(
            "rampTime", this, (o,x) => x,
            {
                documentation: "Time it takes (in ms) to reach the value its adding",
                min: 0,
                max: 10000,
                defaultValue: 80
            });

        this.registerParameter(this.rampTimeParameter);

        this.resetAtZeroParameter = new AbstractParameter(
            "resetAtZero", this, (o,x) => x,
            {
                documentation: "Should the accumulator reset to 0 upon reaching step 1 in the pattern",
                min: 0,
                max: 1,
                defaultValue: false
            });

        this.registerParameter(this.resetAtZeroParameter);

        this.initDisabledParameters();
   }

    getInitialValueForParameter(parameter, i=this._parameters.indexOf(parameter)) {
        return this.initialParameterValues[i];
    }

    setInitialValueForParameter(parameter, value) {
        let indexOf = this._parameters.indexOf(parameter);
        this.initialParameterValues[indexOf] = value;
    }

    shouldSkipParameter(i) {
        return false;
    }

    // sets the parameters to the INITIAL values before playing
    resetValues() {
        let bulk = {};

        for (let i in this._parameters) {
            if (this.shouldSkipParameter(i)) {
                continue;
            }
            let parameter = this._parameters[i];
            if (this.initialParameterValues[i] !== undefined) {
                let initialValue = this.initialParameterValues[i];
               if (initialValue < parameter.min ||
                    initialValue > parameter.max) {
                    // the min/max changed while playing so set to avg
                    // of min/max
                    initialValue = (parameter.min + parameter.max) / 2.0;
                }
                if (parameter._wasDisposed) {
                    continue;
                }
                let ret = parameter.set(initialValue, true, true);
                bulk[ret.id] = ret.value;
                if (parameter.depthParameter) {
                    let {depthParameter} = parameter;
                    let initialDepth =this.initialParameterDepths[i];
                    if (initialDepth === undefined) {
                        continue;
                    }
                    if (initialDepth < depthParameter.min ||
                        initialDepth > depthParameter.max) {
                        // the depth min/max changed while playing so set to avg
                        // of min/max
                        initialDepth = (depthParameter.min + depthParameter.max) / 2.0;
                    }
                    ret = depthParameter.set(
                        initialDepth, true, true);
                    bulk[ret.id] = ret.value;
                }else {
                    parameter.depth = this.initialParameterDepths[i];
                }
            } else {
            }
        }
        this.setEffectParameterValues(bulk);
    }

    setEffectParameterValues(bulk) {
        this.track.sequencerEngine.dispatch(
            setEffectParameterValues(bulk));
    }

    stop() {
        this.resetValues();
    }

    unmap(p) {
        let i = this._parameters.indexOf(p);
        this.unmapParameter(i);
    }

    unmapParameter(i) {
        let parameter = this._parameters[i];
        if (!parameter) {
            return;
        }
        parameter.locked = undefined;
        this._parameters.splice(i, 1);
        let depthParameter = this.depthParameters[i];
        this.depthParameters.splice(i, 1);

        let index = this.parameters.indexOf(depthParameter);
        this.parameters.splice(index, 1);
        
        this.onParameterUnmapped(parameter);
        let value = this.initialParameterValues[i];
        this.initialParameterValues.splice(i, 1);
        if (value !== undefined) {
            parameter.set(value, true, false);
        }
        parameter[this.key] = undefined;
        return parameter;
    }

    onParameterUnmapped() {}

    get key() {
        return "isMapped";
    }
   

    setParameter(parameter, i=this._parameters.length) {
        this._parameters[i] = parameter;
        this.initialParameterValues[i] = parameter.get(true);
        if (!this.depthParameters[i]) {
            this.depthParameters[i] = this.newDepthParameter(parameter);
        }
        parameter[this.key]  = this;
    }

    _clear() {
        this.initialParameterValues = [];

        // upon restarting we need to reset the parameter values
        for (let i in this._parameters) {
            if (this.shouldSkipParameter(i)) {
                continue;
            }
            let parameter = this._parameters[i];
            this.clearParameter(parameter);
        }
    }

    /** called BY MutableParameter when the user manually changes the parameter
     * in this case we need to clear the stored parameter so that
     * it doesnt get reset to an old value
     */
    clearParameter(parameter, i) {
        this._clearParameter(parameter, i);
    }

    // sets the initialParameterValues
    // helper called by clearParameter
    _clearParameter(parameter, i=this._parameters.indexOf(parameter)) {
        if (!parameter) {
            return;
        }
        let value = parameter.get(true);
        if (value >= parameter.max) {
            parameter.max = value;
        } else if (value <= parameter.min) {
            parameter.min = value;
        }
        this.initialParameterValues[i] = value;
        if (parameter.depthParameter) {
            let depth = parameter.depthParameter.get(true);
            if (isNaN(depth)) {
                this.initialParameterDepths[i] = 1;
            } else {
                this.initialParameterDepths[i] = depth;
            }
        } else {
            this.initialParameterDepths[i] = parameter.depth;
        }
    }

    get resetAtZero() {
        return this.resetAtZeroParameter.get();
    }

    get rampTime() {
        return this.rampTimeParameter.get();
    }

    onNewStep() {
        if (this.triggers.length > 0) {
            this.triggers.length = 0;
        }
    }

    clear() {
        super.clear();
        this._clear();
    }

    // getParameterValueToSet should be implemented in the class using this
    getParameterValueToSet(i) { return 0; }

    setParameterValues(time) {
        let bulk ={};
        for (let i in this._parameters) {
            i = parseInt(i);
            if (this.parametersDisabled.includes(i)) {
                continue;
            }

            let _value =  this.getParameterValueToSet(i); 
            let parameter = this._parameters[i];

            if (isNaN(_value) || parameter.get(true) === _value) {
                // dont set the parameter if it has not changed
                continue;
            }

            let {max, min} = parameter;

            let {id, value} = parameter.set(
                Math.min(max, Math.max(min, _value)),
                true,
                true,
                time,
                this.rampTime / 1000.0);
            bulk[id] = value;
        }

        // update redux with these new values
        this.track.sequencerEngine.parametersToUpdate = {
            ... this.track.sequencerEngine.parametersToUpdate,
            ... bulk
        };
    }

    loadParameters(parameters, initialLoad=false) {
        let newMappings = {};

        let subSequence = this.track && this.track.subSequence;
        if (!parameters || (this.subSequence && !this.subSequence.tracks)) {
            if (this.subSequence.getRootSubSequence) {
                subSequence = this.subSequence.getRootSubSequence();
            } else {
                return {lostParameters: [], newMappings};
            }
        }
        // upon initial load, dont use the context to try to find parameters
        // if the ids dont match
        // but upon second load, if we dont find the id we want, we use the context
        // to find a best match
        let sequencerEngine = this.track.sequencerEngine;
        let lostParameters = [];
        for (let parameter of parameters) {
            let id = typeof parameter === "object" ? parameter.id : parameter;
            // need to find the parameter within the tracks
            let foundParameter = undefined;
            if (parameter.name === "BPM") {
                foundParameter = sequencerEngine.bpmParameter;
            }

            let f = sequencerEngine.parameters.find(x => x.id === id);
            if (f) {
                foundParameter = f;
            }
            let tracks = this.track.getAllParameters ?
                [this.track, ... subSequence.tracks.filter(t => t !== this.track)]
                : subSequence.tracks;
            for (let t of tracks) {
                let parameters = t.getAllParameters();
                let allParameters = [];
                parameters.forEach(
                    p => {
                        allParameters.push(p);
                        if (p.depthParameter) {
                            allParameters.push(p.depthParameter);
                        }
                    });
                let _foundParameter = allParameters.find(p => id === p.id);
                if (_foundParameter) {
                    foundParameter = _foundParameter;
                    break;
                }
            }

            // TODO: if zobject is a sub-patch look thru that as well
            let pattrParameters = subSequence.pattr.getNestedParameters();
            let _found = pattrParameters.find(p => p.id === id);
            if (_found) {
                foundParameter = _found;
            }
            
            for (let t of [... subSequence.zObjects, ... subSequence.zMessages]) {
                let _foundParameter = t.getAllParameters().find(p => id === p.id);
                if (_foundParameter) {
                    foundParameter = _foundParameter;
                    break;
                }
            }

            if (!foundParameter) {
                // then we need to look at lfo parameters and envelope parameters
                for (let lfo of subSequence.lfos) {
                    lfo = lfo.lfo;
                    let _foundParameter = lfo.parameters.find(p => id === p.id);
                    if (_foundParameter) {
                        foundParameter = _foundParameter;
                        break;
                    }
                }

                if (!foundParameter) {
                    for (let track of subSequence.tracks) {
                        if (track.lfosTrack) {
                            for (let lfo of track.lfosTrack.lfos) {
                                lfo = lfo.lfo;
                                let _foundParameter = lfo.parameters
                                    .find(p => id === p.id);
                                if (_foundParameter) {
                                    foundParameter = _foundParameter;
                                    break;
                                }
                            }
                        }
                    }
                }
            }

            if (!foundParameter) {
                for (let subSequence of this.track.sequencerEngine.subSequences) {
                    let effects = [
                        ... subSequence.midiEffects.shadowEffects,
                        ... subSequence.midiEffects.effects, subSequence.playhead];
                    let _parameters = [
                        ... effects.flatMap(x => x.getParameters()),
                        subSequence.midiEffects.transposeParameter,
                        ... subSequence.pads.filter(
                            p => p.fxMacro).
                            map(pad => pad.fxMacro.macroParameter)
                    ];
                    foundParameter = _parameters.find(p => id === p.id);
                    if (foundParameter) {
                        break;
                    }
                }
            }
            if (!foundParameter) {
                let _parameters = sequencerEngine.sends.sends.flatMap(x => x.effects)
                   .flatMap(x => x.getParameters());
                foundParameter = _parameters.find(p => id === p.id);
            }

            if (!foundParameter) {
                for (let env of subSequence.envelopes) {
                    let _foundParameter = env.parameters.find(p => id === p.id);
                    if (_foundParameter) {
                        foundParameter = _foundParameter;
                        break;
                    }
                }

                for (let track of subSequence.tracks) {
                    if (track.envelopesTrack) {
                        for (let env of track.envelopesTrack.envelopes) {
                            env = env.envelope;
                            let _foundParameter = env.parameters
                                .find(p => id === p.id);
                            if (_foundParameter) {
                                foundParameter = _foundParameter;
                                break;
                            }
                        }
                    }
                }
            }

            if (!foundParameter) {
                let _parameters = sequencerEngine.channelMixer
                    .masterChannel.effectsChain.effects
                    .flatMap(x => x.getParameters().flatMap(
                        x => x.depthParameter ? [x, x.depthParameter] : x));
                let _foundParameter = _parameters.find(p => id === p.id);
                if (_foundParameter) {
                    foundParameter = _foundParameter;
                }
            }


            if (!foundParameter && !initialLoad && typeof parameter === "object") {
                foundParameter = findParameterWithContext(
                    parameter, this.track, this.track.sequencerEngine);
                // need to map the id to this found parameter
                if (foundParameter) {
                    newMappings[id] = foundParameter;
                    let {x,y} = parameter.coordinates;
                    // foundParameter.setCoordinates(x, y);
                }
            } 

            // check if its lfo phase (super inefficient)
            if (foundParameter && foundParameter.name.includes("Phase")) {
                continue;
            }
            
            if (foundParameter) {
                this.setParameter(foundParameter);
            } else {
                lostParameters.push(parameter);
            }
        }

        this.sortParameters();

        return {lostParameters, newMappings};
    }

    sortParameters() {
        if (!this.parametersJson) {
            return;
        }
        let parameters = this.parametersJson.map(
            ({id}, i) => this._parameters.find(
                p => p && p.id === id))
            .filter(p => p);
        this._parameters = parameters;
    }

    loadDepthJson(json, isCopy) {
        if (json.depthParameters) {
            let i = 0;
            for (let _depthParameter of json.depthParameters) {
                let depthParameter = this.newDepthParameter();
                depthParameter.fromJson(_depthParameter, this.track.sequencerEngine, isCopy);
                this.depthParameters[i++] = depthParameter;
            }
        }
    }

    getMappableJson(isPreset) {
        return {
            parameters: this._parameters.map(x => ({
                id: x.id,
                context: !isPreset ? undefined : x.context && x.context.getJson(),
                name: !isPreset ? undefined : x.name,
                coordinates: x.getCoordinates()
            })),
            depthParameters: this.depthParameters.map(
                x => x.getJson())
        };
    }

    getJson(isPreset) {
        let json = {
            ... super.getJson(isPreset),
            ... this.getMappableJson(isPreset),
        };
        return json;
    }

    fromJson(json, isCopy) {
        delete json.depth;
        this.parametersJson = json.parameters;
        let bulk = super.fromJson(json, isCopy);

        // keep track of any parameters that we've lost so we can search for them
        // at the end of load

        this.lostParameters = this.loadParameters(json.parameters, true).lostParameters;
        this.loadDepthJson(json, isCopy);

        return bulk;
    }

    // get a bunch of parameters
    randomlyMapParameters(device, numParameters=200, parameters=getTrackParameters(device, this)) {
        /*
        let lfos = this.track.sequencerEngine.lfos.map(x => x.lfo);
        for (let lfo of lfos) {
            parameters = [
                ... lfo.parameters.filter(x => !PARAMS_TO_IGNORE.includes(x.name)),
                ... parameters];
        }
        */

        // if any of these are formants, then make sure to only
        // add them if they are enabled
        let _parameters = [];
        for (let p of parameters) {
            if (p.context && p.context.formant) {
                let formant = p.context.formant;
                if (!formant.enabledParameter.get()) {
                    continue;
                } 
            }
            _parameters.push(p);
        }

        shuffle(_parameters);
        _parameters = Array.from(new Set(_parameters));
        _parameters = _parameters.slice(0, numParameters);

        // set the parameters
        _parameters.forEach(
            p => this.setParameter(p));

        return _parameters;
    }

    dispose() {
        // first get rid of all mappings
        while (this._parameters.length > 0) {
            this.unmapParameter(0);
        }

        if (super.dispose) {
            super.dispose();
        }
    }

    replaceParameter(parameter) {
        for (let i in this._parameters) {
            if (this._parameters[i].id === parameter.id) {
                this._parameters[i] = parameter;
                return;
            }
        }
    }

     shouldOutputSkip(time, stepNumber) {
         if (stepNumber === 0 && this.resetAtZero) {
             this.stop();
             this.clear();
             return true;
         }
         
         if (this.triggers.length === 0) {
             return true;
         }
         
         return false;
     }
}

